// ==============================================================================
// FILE: src/pages/ReportsPage.tsx (DEFINITIVE FIX - Re-instated Pie Chart)
// PURPOSE: A fully interactive, multi-level drill-down reporting tool that
//          correctly displays both a Pie and Bar chart on the initial view.
// ==============================================================================
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Card, CardContent,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Breadcrumbs, Link
} from '@mui/material';
import {
    PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import {
    Assessment as ReportIcon,
    TrendingUp as TrendingUpIcon,
    Category as CategoryIcon,
    AttachMoney as AttachMoneyIcon,
    Insights as InsightsIcon,
    NavigateNext
} from '@mui/icons-material';
import { useAppContext } from '../contexts/AppContext';
import { ReportRequest, ReportResponseItem, TransactionDetail } from '../types';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF1943'];
const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
};

const KpiCard = ({ title, value, icon, color }: { title: string, value: string | number, icon: React.ReactNode, color: string }) => (
    <Card elevation={3} sx={{ height: '100%' }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ p: 1.5, borderRadius: '50%', backgroundColor: color, color: '#fff', mr: 2, display: 'flex' }}>{icon}</Box>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>{title}</Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

export default function ReportsPage(): JSX.Element {
  const { apiService, showNotification } = useAppContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<ReportResponseItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<string>('Generate a report to see AI-powered insights.');
  const [loadingSummary, setLoadingSummary] = useState<boolean>(false);
  
  const [drillDownLevel, setDrillDownLevel] = useState(0);
  const [drillDownFilter, setDrillDownFilter] = useState<string | null>(null);
  const [drillDownData, setDrillDownData] = useState<any[]>([]);
  const [loadingDrillDown, setLoadingDrillDown] = useState<boolean>(false);
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>(['Overall']);

  const today = new Date();
  const thirtyDaysAgo = new Date(new Date().setDate(today.getDate() - 30));

  const [filters, setFilters] = useState<ReportRequest>({
    start_date: thirtyDaysAgo.toISOString().split('T')[0],
    end_date: today.toISOString().split('T')[0],
    group_by: 'expense_type',
  });

  const handleFilterChange = (e: any) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name as string]: value }));
  };

  const resetDrillDown = () => {
    setDrillDownLevel(0);
    setDrillDownFilter(null);
    setDrillDownData([]);
    setBreadcrumbs(['Overall']);
  };

  const handleGenerateReport = useCallback(async (isInitialLoad = false) => {
    if (!isInitialLoad) setLoading(true);
    setLoadingSummary(true);
    setError(null);
    setReportData([]);
    resetDrillDown();
    setAiSummary('');
    try {
      const response = await apiService.getExpenseSummaryReport(filters);
      setReportData(response.data.map(d => ({...d, total_amount: Number(d.total_amount)})));
      if (response.data.length === 0) {
        if (!isInitialLoad) showNotification('No data found for the selected criteria.', 'info');
        setAiSummary('No data available to generate insights.');
      } else {
        const summaryResponse = await apiService.getAiSummary(response.data, filters);
        setAiSummary(summaryResponse.summary);
      }
    } catch (err: any) {
      const errorMessage = (err as Error).message || 'Failed to generate report.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      if (!isInitialLoad) setLoading(false);
      setLoadingSummary(false);
    }
  }, [apiService, showNotification, filters]);

  useEffect(() => {
    handleGenerateReport(true);
  }, []);
  
  const handleChartClick = useCallback(async (data: any) => {
    if (!data || !data.activePayload || !data.activePayload[0]) return;
    
    const categoryName = data.activePayload[0].payload.category;
    setLoadingDrillDown(true);

    if (drillDownLevel === 0) {
        try {
            let response;
            // --- THIS IS THE CORE BUG FIX ---
            // Check how the report is grouped and call the correct API function.
            if (filters.group_by === 'employee_name') {
                // If grouped by employee, get the expense breakdown for that employee.
                response = await apiService.getExpenseBreakdownForEmployee(filters, categoryName);
            } else {
                // Otherwise, get the employee breakdown for the selected expense type.
                response = await apiService.getEmployeeBreakdownForExpense(filters, categoryName);
            }
            // --- END OF FIX ---
            setDrillDownData(response.data.map((d: ReportResponseItem) => ({...d, total_amount: Number(d.total_amount)})));
            setDrillDownLevel(1);
            setDrillDownFilter(categoryName);
            setBreadcrumbs(['Overall', categoryName]);
        } catch (err: any) {
            showNotification(err.message || 'Failed to fetch drill-down data.', 'error');
        }
    } else if (drillDownLevel === 1) {
        try {
            // The final drill-down level (transactions) logic remains the same.
            // The `drillDownFilter` correctly holds the context from the first level.
            let employeeName = '';
            let expenseType = '';

            if (filters.group_by === 'employee_name') {
                employeeName = drillDownFilter!;
                expenseType = categoryName;
            } else {
                employeeName = categoryName;
                expenseType = drillDownFilter!;
            }

            const response = await apiService.getTransactionDetails(filters, expenseType, employeeName);
            setDrillDownData(response.data);
            setDrillDownLevel(2);
            setBreadcrumbs(['Overall', drillDownFilter!, categoryName]);
        } catch (err: any) {
            showNotification(err.message || 'Failed to fetch transaction details.', 'error');
        }
    }
    setLoadingDrillDown(false);
  }, [apiService, showNotification, filters, drillDownLevel, drillDownFilter]);

  const handleBreadcrumbClick = (index: number) => {
      if (index < drillDownLevel) {
          if (index === 0) {
              resetDrillDown();
          } else if (index === 1) {
              // This part of logic may need adjustment if more levels are added
              setDrillDownLevel(1);
              setBreadcrumbs(prev => prev.slice(0, 2));
          }
      }
  };

  const { totalAmount, categoryCount, topCategory } = useMemo(() => {
    if (!reportData || reportData.length === 0) return { totalAmount: 0, categoryCount: 0, topCategory: 'N/A' };
    const total = reportData.reduce((sum, item) => sum + item.total_amount, 0);
    const topItem = [...reportData].sort((a, b) => b.total_amount - a.total_amount)[0];
    return { totalAmount: total, categoryCount: reportData.length, topCategory: topItem.category };
  }, [reportData]);

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>Advanced Reports</Typography>
        </Box>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth size="small" disabled={drillDownLevel > 0}><InputLabel>Group By</InputLabel><Select name="group_by" value={filters.group_by} label="Group By" onChange={handleFilterChange}><MenuItem value="expense_type">Expense Type</MenuItem><MenuItem value="employee_name">Employee</MenuItem><MenuItem value="contract_name">Contract</MenuItem></Select></FormControl></Grid>
                <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Start Date" name="start_date" type="date" value={filters.start_date} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" disabled={drillDownLevel > 0} /></Grid>
                <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="End Date" name="end_date" type="date" value={filters.end_date} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" disabled={drillDownLevel > 0} /></Grid>
                <Grid item xs={12} sm={6} md={3}><Button variant="contained" onClick={() => handleGenerateReport(false)} disabled={loading || drillDownLevel > 0} startIcon={<ReportIcon />} fullWidth>{loading ? 'Generating...' : 'Generate Report'}</Button></Grid>
            </Grid>
        </Paper>

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && (
            <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={4}><KpiCard title="Total Amount Reported" value={formatCurrency(totalAmount)} icon={<AttachMoneyIcon />} color="#0088FE" /></Grid>
                    <Grid item xs={12} sm={6} md={4}><KpiCard title="Number of Categories" value={categoryCount} icon={<CategoryIcon />} color="#00C49F" /></Grid>
                    <Grid item xs={12} sm={6} md={4}><KpiCard title="Top Spending Category" value={topCategory} icon={<TrendingUpIcon />} color="#FFBB28" /></Grid>
                </Grid>
                <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }} variant="outlined">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}><InsightsIcon color="primary" sx={{ mr: 1 }} /><Typography variant="h6">AI-Powered Insights</Typography></Box>
                    {loadingSummary ? <CircularProgress size={20} /> : <Typography dangerouslySetInnerHTML={{ __html: aiSummary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />}
                </Paper>
                
                <Paper sx={{ p: 2, height: 500, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                        <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
                            {breadcrumbs.map((crumb, index) => (
                                index === breadcrumbs.length - 1 ? 
                                <Typography key={index} color="text.primary">{crumb}</Typography> :
                                <Link key={index} component="button" onClick={() => handleBreadcrumbClick(index)} sx={{textDecoration: 'none'}}>{crumb}</Link>
                            ))}
                        </Breadcrumbs>
                    </Box>

                    {loadingDrillDown ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> :
                    
                    drillDownLevel === 0 ? (
                        <Grid container spacing={2} sx={{flex: 1}}>
                            <Grid item xs={12} md={5}>
                                <Typography variant="h6" align="center">Expense Distribution</Typography>
                                <ResponsiveContainer width="100%" height="90%">
                                    <PieChart>
                                        <Pie data={reportData} cx="50%" cy="50%" outerRadius={120} innerRadius={70} dataKey="total_amount" nameKey="category" onClick={handleChartClick}>
                                            {reportData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                                        </Pie>
                                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Typography variant="h6" align="center">Amount by Category</Typography>
                                <ResponsiveContainer width="100%" height="90%">
                                    <BarChart data={reportData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} onClick={handleChartClick}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="category" />
                                        <YAxis tickFormatter={(value) => formatCurrency(Number(value))} />
                                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                                        <Bar dataKey="total_amount" name="Total Amount">
                                            {reportData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Grid>
                        </Grid>
                    ) : drillDownLevel === 1 ? (
                         <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={drillDownData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }} onClick={handleChartClick}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="category" angle={-45} textAnchor="end" interval={0} height={100} />
                                <YAxis tickFormatter={(value) => formatCurrency(Number(value))} />
                                <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                                <Bar dataKey="total_amount" name="Total Amount">
                                    {drillDownData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <TableContainer component={Paper} sx={{ maxHeight: 380 }}>
                            <Table stickyHeader>
                                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Description</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {(drillDownData as TransactionDetail[]).map((row, index) => (
                                        <TableRow hover key={index}>
                                            <TableCell>{row.transaction_date ? new Date(row.transaction_date).toLocaleDateString() : 'N/A'}</TableCell>
                                            <TableCell>{row.description || 'N/A'}</TableCell>
                                            <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </>
        )}
    </Paper>
  );
}

